import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import SongCard from '../components/SongCard';
import LibraryScopeFilter, { useLibraryScope } from '../components/LibraryScopeFilter';
import {
  AlphaFilterBar,
  AlphaFilterButton,
  AlbumsActionLink,
  AlbumsActions,
  AlbumsMeta,
  AlbumsViewTitle,
  LibraryPageLayout,
  LibrarySearchInput,
  LibrarySectionCard,
  LibrarySongList,
  LibraryStatusText,
  SortControlBar,
  SortControlButton,
} from '../components/styled';

const ALPHA_RANGES = [
  { label: 'ABC', value: 'abc', letters: 'ABC' },
  { label: 'DEF', value: 'def', letters: 'DEF' },
  { label: 'GHI', value: 'ghi', letters: 'GHI' },
  { label: 'JKL', value: 'jkl', letters: 'JKL' },
  { label: 'MNO', value: 'mno', letters: 'MNO' },
  { label: 'PQR', value: 'pqr', letters: 'PQR' },
  { label: 'STU', value: 'stu', letters: 'STU' },
  { label: 'VWX', value: 'vwx', letters: 'VWX' },
  { label: 'YZ', value: 'yz', letters: 'YZ' },
  { label: '#', value: 'other', letters: '' },
];

function matchesAlphaRange(title, alphaRange) {
  if (!alphaRange) {
    return true;
  }

  const firstCharacter = (title || '').trim().charAt(0).toUpperCase();
  const range = ALPHA_RANGES.find((currentRange) => currentRange.value === alphaRange);

  if (!range) {
    return true;
  }

  if (range.value === 'other') {
    return !/^[A-Z]$/.test(firstCharacter);
  }

  return range.letters.includes(firstCharacter);
}

function buildSongRoute(songId, albumId, query, alphaRange, sortBy, bandId = '', userId = '') {
  const searchParams = new URLSearchParams();

  if (albumId) {
    searchParams.set('albumId', albumId);
  }

  if (query) {
    searchParams.set('q', query);
  }

  if (alphaRange) {
    searchParams.set('alpha', alphaRange);
  }

  if (sortBy && sortBy !== 'title') {
    searchParams.set('sort', sortBy);
  }

  const search = searchParams.toString();

  const path = bandId
    ? `/bands/${bandId}/song/${songId}`
    : userId
      ? `/users/${userId}/song/${songId}`
      : `/song/${songId}`;
  return search ? `${path}?${search}` : path;
}

function LibraryPage() {
  const location = useLocation();
  const { albumId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [songs, setSongs] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const isAlbumsView = location.pathname.startsWith('/library/albums');
  const isSongsView = !isAlbumsView;
  const isAlbumDetailRoute = Boolean(albumId);
  const query = searchParams.get('q') || '';
  const albumsQuery = searchParams.get('albumsQ') || '';
  const alphaRange = searchParams.get('alpha') || '';
  const albumsAlphaRange = searchParams.get('albumsAlpha') || '';
  const sortBy = searchParams.get('sort') === 'artist' ? 'artist' : 'title';
  const libraryScope = useLibraryScope(searchParams, setSearchParams, {
    enabled: true,
    allowUserScope: isSongsView,
  });

  useEffect(() => {
    if (libraryScope.isLoadingScopeOptions) return undefined;
    let isActive = true;

    const loadLibraryData = async () => {
      setIsLoading(true);

      try {
        const [songsResponse, albumsResponse] = await Promise.all([
          libraryScope.libraryApi.getSongs(),
          libraryScope.libraryApi.getAlbums(),
        ]);

        if (!isActive) {
          return;
        }

        const nextSongs = [...(songsResponse?.songs || [])].sort(
          (leftSong, rightSong) =>
            (leftSong.title || '').localeCompare(rightSong.title || '')
        );
        const nextAlbums = [...(albumsResponse?.albums || [])].sort(
          (leftAlbum, rightAlbum) =>
            (leftAlbum.title || '').localeCompare(rightAlbum.title || '')
        );

        setSongs(nextSongs);
        setAlbums(nextAlbums);
        setErrorMessage('');
      } catch (error) {
        if (!isActive) {
          return;
        }

        setSongs([]);
        setAlbums([]);

        if (error.status !== 401) {
          setErrorMessage(error.message || 'Failed to load library data.');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLibraryData();

    return () => {
      isActive = false;
    };
  }, [libraryScope.isLoadingScopeOptions, libraryScope.libraryApi]);

  const trimmedQuery = query.trim().toLowerCase();

  const filteredSongs = useMemo(
    () =>
      songs
        .filter((song) => {
        if (!matchesAlphaRange(song.title || '', alphaRange)) {
          return false;
        }

        if (!trimmedQuery) {
          return true;
        }

        const haystack = [song.title || '', song.artist || '']
          .join(' ')
          .toLowerCase();

        return haystack.includes(trimmedQuery);
        })
        .sort((leftSong, rightSong) => {
          if (sortBy === 'artist' && !alphaRange) {
            return (
              (leftSong.artist || '').localeCompare(rightSong.artist || '') ||
              (leftSong.title || '').localeCompare(rightSong.title || '')
            );
          }

          return (
            (leftSong.title || '').localeCompare(rightSong.title || '') ||
            (leftSong.artist || '').localeCompare(rightSong.artist || '')
          );
        }),
    [songs, alphaRange, sortBy, trimmedQuery]
  );

  const filteredAlbums = useMemo(
    () =>
      albums.filter((album) => {
        if (!trimmedQuery) {
          return true;
        }

        const haystack = [
          album.title || '',
          album.artist || '',
          ...(album.songs || []).map((song) =>
            [song.title || '', song.artist || ''].join(' ')
          ),
        ]
          .join(' ')
          .toLowerCase();

        return haystack.includes(trimmedQuery);
      }),
    [albums, trimmedQuery]
  );

  const selectedAlbumEntry = useMemo(
    () => albums.find((album) => album._id === albumId) || null,
    [albumId, albums]
  );

  const selectedAlbumSongs = useMemo(
    () =>
      (selectedAlbumEntry?.songs || [])
        .filter((song) => {
          if (!trimmedQuery) {
            return true;
          }

          const haystack = [song.title || '', song.artist || '']
            .join(' ')
            .toLowerCase();

          return haystack.includes(trimmedQuery);
        }),
    [selectedAlbumEntry, trimmedQuery]
  );

  const selectedAlbumRouteMissing =
    isAlbumDetailRoute && !isLoading && !selectedAlbumEntry;

  const searchPlaceholder = isAlbumsView
    ? selectedAlbumEntry
      ? `Search songs in ${selectedAlbumEntry.title || 'album'}`
      : 'Search albums'
    : 'Search songs';
  const albumListSearchParams = new URLSearchParams();
  if (albumsQuery) albumListSearchParams.set('q', albumsQuery);
  if (albumsAlphaRange) albumListSearchParams.set('alpha', albumsAlphaRange);
  if (libraryScope.scope === 'band' && libraryScope.selectedBand) {
    albumListSearchParams.set('scope', 'band');
    albumListSearchParams.set('bandId', libraryScope.selectedBand._id);
  }
  const albumListSearch = albumListSearchParams.toString();
  const albumListRoute = `/albums${albumListSearch ? `?${albumListSearch}` : ''}`;
  const albumContextSearchParams = new URLSearchParams();

  if (albumsQuery) {
    albumContextSearchParams.set('albumsQ', albumsQuery);
  }

  if (albumsAlphaRange) {
    albumContextSearchParams.set('albumsAlpha', albumsAlphaRange);
  }

  const albumContextSearch = albumContextSearchParams.toString()
    ? `?${albumContextSearchParams.toString()}`
    : '';

  const handleSearchChange = (event) => {
    const nextQuery = event.target.value;
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextQuery) {
      nextSearchParams.set('q', nextQuery);
    } else {
      nextSearchParams.delete('q');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleAlphaChange = (nextAlphaRange) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    const resolvedAlphaRange =
      alphaRange === nextAlphaRange ? '' : nextAlphaRange;

    if (resolvedAlphaRange) {
      nextSearchParams.set('alpha', resolvedAlphaRange);
    } else {
      nextSearchParams.delete('alpha');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  const handleSortChange = (nextSortBy) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextSortBy === 'artist') {
      nextSearchParams.set('sort', 'artist');
    } else {
      nextSearchParams.delete('sort');
    }

    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <LibraryPageLayout>
      <LibrarySectionCard>
        {isSongsView || !isAlbumDetailRoute ? <LibraryScopeFilter {...libraryScope} /> : null}

        {libraryScope.bandsError ? (
          <LibraryStatusText $error>{libraryScope.bandsError}</LibraryStatusText>
        ) : null}

        {isSongsView && libraryScope.usersError ? (
          <LibraryStatusText $error>{libraryScope.usersError}</LibraryStatusText>
        ) : null}

        {isAlbumsView && isAlbumDetailRoute && selectedAlbumEntry ? (
          <>
            {selectedAlbumEntry.artist ? (
              <AlbumsMeta>{selectedAlbumEntry.artist}</AlbumsMeta>
            ) : null}
            <AlbumsViewTitle>
              {selectedAlbumEntry.title || 'Untitled album'}
            </AlbumsViewTitle>
          </>
        ) : null}

        <LibrarySearchInput
          type="search"
          value={query}
          onChange={handleSearchChange}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
        />

        {isSongsView ? (
          <AlphaFilterBar aria-label="Filter songs alphabetically">
            {ALPHA_RANGES.map((range) => (
              <AlphaFilterButton
                key={range.value}
                type="button"
                $active={alphaRange === range.value}
                onClick={() => handleAlphaChange(range.value)}
              >
                {range.label}
              </AlphaFilterButton>
            ))}
          </AlphaFilterBar>
        ) : null}

        {isSongsView ? (
          <SortControlBar aria-label="Sort songs">
            <SortControlButton
              type="button"
              $active={sortBy === 'title'}
              onClick={() => handleSortChange('title')}
            >
              Song Title
            </SortControlButton>
            <SortControlButton
              type="button"
              $active={sortBy === 'artist'}
              onClick={() => handleSortChange('artist')}
            >
              Artist
            </SortControlButton>
          </SortControlBar>
        ) : null}

        {errorMessage ? (
          <LibraryStatusText $error>{errorMessage}</LibraryStatusText>
        ) : null}

        {!errorMessage && isLoading ? (
          <LibraryStatusText>Loading songs...</LibraryStatusText>
        ) : null}

        {!errorMessage && !isLoading && isSongsView && filteredSongs.length === 0 ? (
          <LibraryStatusText>
            {query.trim()
              ? 'No songs matched your search.'
              : alphaRange
                ? 'No songs matched this letter range.'
              : libraryScope.scope === 'band'
                ? 'No songs are saved for this band yet.'
                : libraryScope.scope === 'user'
                  ? 'No songs are saved for this user yet.'
                : 'No songs are saved for this account yet.'}
          </LibraryStatusText>
        ) : null}

        {!errorMessage &&
        !isLoading &&
        isAlbumsView &&
        !isAlbumDetailRoute &&
        filteredAlbums.length === 0 ? (
          <LibraryStatusText>
            {query.trim()
              ? 'No albums matched your search.'
              : 'No albums are available yet.'}
          </LibraryStatusText>
        ) : null}

        {!errorMessage && selectedAlbumRouteMissing ? (
          <LibraryStatusText $error>
            This album no longer exists or is unavailable.
          </LibraryStatusText>
        ) : null}

        {!errorMessage &&
        !isLoading &&
        isAlbumsView &&
        isAlbumDetailRoute &&
        selectedAlbumEntry &&
        selectedAlbumSongs.length === 0 ? (
          <LibraryStatusText>
            {query.trim()
              ? 'No songs in this album matched your search.'
              : 'This album does not contain any songs.'}
          </LibraryStatusText>
        ) : null}

        {isSongsView && filteredSongs.length > 0 ? (
          <LibrarySongList>
            {filteredSongs.map((song) => (
              <SongCard
                key={song._id}
                song={song}
                to={buildSongRoute(
                  song._id,
                  '',
                  query,
                  alphaRange,
                  sortBy,
                  libraryScope.scope === 'band' ? libraryScope.selectedBand?._id : '',
                  libraryScope.scope === 'user' ? libraryScope.selectedUser?.userId : ''
                )}
                compact
              />
            ))}
          </LibrarySongList>
        ) : null}

        {/*{isAlbumsView && !isAlbumDetailRoute && filteredAlbums.length > 0 ? (*/}
        {/*  <LibraryAlbumList>*/}
        {/*    {filteredAlbums.map((album) => (*/}
        {/*      <LibraryAlbumCardButton*/}
        {/*        key={album._id}*/}
        {/*        type="button"*/}
        {/*        onClick={() => handleAlbumOpen(album._id)}*/}
        {/*      >*/}
        {/*        <LibraryAlbumCardMeta>{album.artist || 'Album'}</LibraryAlbumCardMeta>*/}
        {/*        <LibraryAlbumCardTitle>*/}
        {/*          {album.title || 'Untitled album'}*/}
        {/*        </LibraryAlbumCardTitle>*/}
        {/*      </LibraryAlbumCardButton>*/}
        {/*    ))}*/}
        {/*  </LibraryAlbumList>*/}
        {/*) : null}*/}

        {isAlbumsView &&
        isAlbumDetailRoute &&
        selectedAlbumEntry &&
        selectedAlbumSongs.length > 0 ? (
          <LibrarySongList>
            {selectedAlbumSongs.map((song) => (
              <SongCard
                key={song._id}
                song={song}
                to={buildSongRoute(
                  song._id,
                  selectedAlbumEntry._id,
                  query,
                  alphaRange,
                  sortBy,
                  libraryScope.scope === 'band' ? libraryScope.selectedBand?._id : ''
                )}
                compact
              />
            ))}
          </LibrarySongList>
        ) : null}

        {isAlbumsView && isAlbumDetailRoute && selectedAlbumEntry ? (
          <AlbumsActions>
            <AlbumsActionLink as={Link} to={albumListRoute}>
              Back to Albums
            </AlbumsActionLink>
            {libraryScope.scope !== 'band' || libraryScope.selectedBand?.canManage ? (
              <AlbumsActionLink
                as={Link}
                to={libraryScope.scope === 'band'
                  ? `/bands/${libraryScope.selectedBand._id}/albums/${selectedAlbumEntry._id}/edit`
                  : `/albums/${selectedAlbumEntry._id}/edit${albumContextSearch}`}
              >
                Edit Album
              </AlbumsActionLink>
            ) : null}
          </AlbumsActions>
        ) : null}
      </LibrarySectionCard>
    </LibraryPageLayout>
  );
}

export default LibraryPage;
